import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Pencil, Plus, Save, Trash2, LockKeyhole, CheckCircle2, Eye, EyeOff } from "lucide-react";
import TreatmentsPanel from "@/pages/admin/TreatmentsPanel";
import CategoriesPanel from "@/pages/admin/CategoriesPanel";
import PreferenceCategoriesPanel from "@/pages/admin/PreferenceCategoriesPanel";
import SpaModuleOperationalSettings from "@/pages/admin/SpaModuleOperationalSettings";
import ServiceModulePanel from "@/pages/admin/ServiceModulePanel";
import UnifiedBookingCalendar from "@/pages/admin/UnifiedBookingCalendar";
import CalendarPanel from "@/pages/admin/CalendarPanel";
import BookingsListPanel from "@/pages/admin/BookingsListPanel";
import ModuleResourcesPanel from "@/pages/admin/ModuleResourcesPanel";
import ModuleCrewPanel from "@/pages/admin/ModuleCrewPanel";

const emptyItem = {
  id: null,
  name: "",
  category: "",
  description: "",
  duration_min: "",
  capacity: "",
  location: "",
  price: "",
  price_hidden: false,
  active: true,
  guest_visible: true,
  sort_order: 0,
};

const entitlementLabel = {
  licensed: "Licensed",
  unlicensed: "Unlicensed",
  demo_requested: "Demo requested",
  demo_active: "Demo",
  demo_expired: "Demo ended",
  suspended: "Suspended",
};

function Field({ label, children, hint }) {
  return <label className="block">
    <span className="text-sm font-medium">{label}</span>
    <div className="mt-2">{children}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </label>;
}

export default function ModuleWorkspacePanel({ module: initialModule, onBack }) {
  const { admin } = useAuth();
  const canEdit = ["developer", "admin", "manager"].includes(admin?.role);
  const [module, setModule] = useState(initialModule);
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyItem);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/modules/${initialModule.id}/workspace`);
      setModule(r.data.module || initialModule);
      setSettings(r.data.settings || {});
      setItems(r.data.content || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Could not load module workspace");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [initialModule.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSpa = module?.id === "spa";
  const isService = module?.id === "service";
  const engine = module?.engine;
  const entryWord = useMemo(() => {
    if (engine === "appointment") return "service";
    if (engine === "resource") return "booking option";
    if (engine === "order") return "menu item";
    if (engine === "request") return "request type";
    return "item";
  }, [engine]);

  const editItem = (item = null) => {
    const src = item || emptyItem;
    setEditing(item?.id || "new");
    setForm({
      ...emptyItem,
      ...src,
      duration_min: src.duration_min ?? "",
      capacity: src.capacity ?? "",
      price: src.price ?? "",
    });
  };

  const saveItem = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        duration_min: form.duration_min === "" ? null : Number(form.duration_min),
        capacity: form.capacity === "" ? null : Number(form.capacity),
        price: form.price === "" ? null : Number(form.price),
        sort_order: Number(form.sort_order || 0),
      };
      delete payload.id;
      if (editing === "new") await api.post(`/admin/modules/${module.id}/content`, payload);
      else await api.put(`/admin/modules/${module.id}/content/${editing}`, payload);
      toast.success(editing === "new" ? "Content added" : "Content updated");
      setEditing(null); setForm(emptyItem); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save content"); }
    finally { setBusy(false); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete “${item.name}”?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/modules/${module.id}/content/${item.id}`);
      toast.success("Content removed"); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not delete content"); }
    finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const payload = {
        ...settings,
        default_duration_min: Number(settings.default_duration_min || 0),
        slot_interval_min: Number(settings.slot_interval_min || 0),
        booking_lead_min: Number(settings.booking_lead_min || 0),
        prep_time_min: Number(settings.prep_time_min || 0),
        max_advance_days: Number(settings.max_advance_days || 0),
        open_time: settings.open_time || "08:00",
        close_time: settings.close_time || "22:00",
      };
      delete payload.module_id; delete payload.updated_at; delete payload.updated_by;
      const r = await api.put(`/admin/modules/${module.id}/settings`, payload);
      setSettings(r.data); toast.success("Module settings saved");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save settings"); }
    finally { setBusy(false); }
  };

  if (loading || !settings) return <div className="py-20 text-center text-muted-foreground">Loading module…</div>;

  const usable = ["licensed", "demo_active"].includes(module.entitlement);

  return <div className="space-y-8" data-testid={`module-workspace-${module.id}`}>
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-3 mb-4"><ArrowLeft className="h-4 w-4 mr-2"/>All modules</Button>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
        <div>
          <div className="overline text-accent mb-2">{module.category} · {module.engine}</div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-4xl md:text-5xl">{module.name}</h1>
            <Badge variant={usable ? "default" : "outline"}>{entitlementLabel[module.entitlement] || module.entitlement}</Badge>
          </div>
          <p className="text-muted-foreground mt-3 max-w-3xl">{module.description}</p>
        </div>
      </div>
    </div>

    {!usable && <div className="border border-dashed border-accent/50 bg-accent/5 p-5 flex gap-3">
      <LockKeyhole className="h-5 w-5 text-accent mt-0.5 flex-shrink-0"/>
      <div>
        <div className="font-medium">Commercial pre-configuration is available</div>
        <p className="text-sm text-muted-foreground mt-1">You can fully prepare this module — content, settings and crew coverage — before purchasing it or starting a demo. Nothing becomes visible to guests until the module is entitled and switched on.</p>
      </div>
    </div>}

    {isSpa ? <Tabs defaultValue="calendar" className="space-y-5">
      <TabsList className="h-auto flex-wrap justify-start">
        <TabsTrigger value="calendar">Calendar</TabsTrigger>
        <TabsTrigger value="bookings">Bookings</TabsTrigger>
        <TabsTrigger value="treatments">Treatments</TabsTrigger>
        <TabsTrigger value="categories">Treatment categories</TabsTrigger>
        <TabsTrigger value="crew">Crew</TabsTrigger>
        <TabsTrigger value="settings">Module settings</TabsTrigger>
      </TabsList>
      <TabsContent value="calendar"><CalendarPanel /></TabsContent>
      <TabsContent value="bookings"><BookingsListPanel /></TabsContent>
      <TabsContent value="treatments"><TreatmentsPanel /></TabsContent>
      <TabsContent value="categories"><CategoriesPanel /></TabsContent>
      <TabsContent value="crew"><ModuleCrewPanel moduleId="spa" /></TabsContent>
      <TabsContent value="settings" className="space-y-6">
        <ModuleSettings settings={settings} setSettings={setSettings} module={module} canEdit={canEdit} busy={busy} onSave={saveSettings}/>
        <SpaModuleOperationalSettings canEdit={canEdit}/>
      </TabsContent>
    </Tabs> : isService ? <Tabs defaultValue="guests" className="space-y-5">
      <TabsList className="h-auto flex-wrap justify-start">
        <TabsTrigger value="guests">Guest preferences</TabsTrigger>
        <TabsTrigger value="categories">Preference categories</TabsTrigger>
        <TabsTrigger value="settings">Module settings</TabsTrigger>
      </TabsList>
      <TabsContent value="guests"><ServiceModulePanel /></TabsContent>
      <TabsContent value="categories"><PreferenceCategoriesPanel /></TabsContent>
      <TabsContent value="settings"><ModuleSettings settings={settings} setSettings={setSettings} module={module} canEdit={canEdit} busy={busy} onSave={saveSettings}/></TabsContent>
    </Tabs> : <Tabs defaultValue={(["appointment", "resource"].includes(engine) ? "calendar" : "content")} className="space-y-5">
      <TabsList className="h-auto flex-wrap justify-start">
        {["appointment", "resource"].includes(engine) && <TabsTrigger value="calendar">Calendar</TabsTrigger>}
        {["appointment", "resource"].includes(engine) && <TabsTrigger value="bookings">Bookings</TabsTrigger>}
        <TabsTrigger value="content">{engine === "resource" ? "Booking options" : engine === "appointment" ? "Services" : "Content"}</TabsTrigger>
        {["appointment", "resource"].includes(engine) && <TabsTrigger value="resources">Resources</TabsTrigger>}
        {["appointment", "resource"].includes(engine) && <TabsTrigger value="crew">Crew</TabsTrigger>}
        <TabsTrigger value="settings">Module settings</TabsTrigger>
      </TabsList>
      {["appointment", "resource"].includes(engine) && <TabsContent value="calendar"><UnifiedBookingCalendar moduleId={module.id} title={`${module.name} calendar`} /></TabsContent>}
      {["appointment", "resource"].includes(engine) && <TabsContent value="bookings"><UnifiedBookingCalendar moduleId={module.id} title={`${module.name} bookings`} defaultView="agenda" /></TabsContent>}
      <TabsContent value="content" className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl">{engine === "order" ? "Menu & catalogue" : engine === "request" ? "Request types" : engine === "resource" ? "Bookable resources" : "Services"}</h2>
            <p className="text-sm text-muted-foreground mt-1">These entries can be prepared while the module is unlicensed.</p>
          </div>
          {canEdit && <Button onClick={() => editItem()}><Plus className="h-4 w-4 mr-2"/>Add {entryWord}</Button>}
        </div>

        {editing && canEdit && <div className="border border-border bg-card p-5 md:p-6 space-y-5">
          <div className="font-display text-xl">{editing === "new" ? `New ${entryWord}` : `Edit ${entryWord}`}</div>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm((f) => ({...f, name:e.target.value}))}/></Field>
            <Field label="Category"><Input value={form.category} placeholder={engine === "order" ? "Cocktails, Soft drinks, Breakfast…" : "Optional grouping"} onChange={(e) => setForm((f) => ({...f, category:e.target.value}))}/></Field>
            {(engine === "appointment" || engine === "resource") && <>
              <Field label="Default duration (minutes)"><Input type="number" min="0" value={form.duration_min} onChange={(e) => setForm((f) => ({...f, duration_min:e.target.value}))}/></Field>
              <Field label="Capacity"><Input type="number" min="1" value={form.capacity} onChange={(e) => setForm((f) => ({...f, capacity:e.target.value}))}/></Field>
              <Field label="Location"><Input value={form.location} placeholder="Gym, Sun Deck, Upper Deck…" onChange={(e) => setForm((f) => ({...f, location:e.target.value}))}/></Field>
            </>}
            {engine === "order" && <Field label="Price"><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm((f) => ({...f, price:e.target.value}))}/></Field>}
            <Field label="Display order"><Input type="number" value={form.sort_order} onChange={(e) => setForm((f) => ({...f, sort_order:e.target.value}))}/></Field>
          </div>
          <Field label="Description"><Textarea value={form.description} onChange={(e) => setForm((f) => ({...f, description:e.target.value}))}/></Field>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({...f, active:v}))}/>Active</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.guest_visible} onCheckedChange={(v) => setForm((f) => ({...f, guest_visible:v}))}/>Guest visible</label>
            {engine === "order" && <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.price_hidden} onCheckedChange={(v) => setForm((f) => ({...f, price_hidden:v}))}/>Hide price</label>}
          </div>
          <div className="flex gap-2">
            <Button disabled={busy} onClick={saveItem}><Save className="h-4 w-4 mr-2"/>Save</Button>
            <Button variant="outline" disabled={busy} onClick={() => {setEditing(null);setForm(emptyItem);}}>Cancel</Button>
          </div>
        </div>}

        {items.length === 0 ? <div className="border border-border bg-card p-8 text-muted-foreground">No content yet. Add the first {entryWord} to prepare this module.</div> :
          <div className="grid lg:grid-cols-2 gap-3">{items.map((item) => <div key={item.id} className="border border-border bg-card p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-display text-xl">{item.name}</div>
                  {item.category && <Badge variant="outline">{item.category}</Badge>}
                  {!item.active && <Badge variant="outline">Inactive</Badge>}
                  {!item.guest_visible && <Badge variant="outline"><EyeOff className="h-3 w-3 mr-1"/>Crew only</Badge>}
                  {item.guest_visible && <Badge variant="outline"><Eye className="h-3 w-3 mr-1"/>Guest</Badge>}
                </div>
                {item.description && <p className="text-sm text-muted-foreground mt-2">{item.description}</p>}
                <div className="text-xs text-muted-foreground mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  {item.location && <span>{item.location}</span>}
                  {item.duration_min != null && <span>{item.duration_min} min</span>}
                  {item.capacity != null && <span>Capacity {item.capacity}</span>}
                  {item.price != null && !item.price_hidden && <span>{item.price} {settings.currency || "EUR"}</span>}
                </div>
              </div>
              {canEdit && <div className="flex gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={() => editItem(item)}><Pencil className="h-4 w-4"/></Button>
                <Button size="sm" variant="ghost" onClick={() => removeItem(item)}><Trash2 className="h-4 w-4 text-destructive"/></Button>
              </div>}
            </div>
          </div>)}</div>}
      </TabsContent>
      {["appointment", "resource"].includes(engine) && <TabsContent value="resources"><ModuleResourcesPanel moduleId={module.id} /></TabsContent>}
      {["appointment", "resource"].includes(engine) && <TabsContent value="crew"><ModuleCrewPanel moduleId={module.id} /></TabsContent>}
      <TabsContent value="settings"><ModuleSettings settings={settings} setSettings={setSettings} module={module} canEdit={canEdit} busy={busy} onSave={saveSettings}/></TabsContent>
    </Tabs>}
  </div>;
}

function ModuleSettings({ settings, setSettings, module, canEdit, busy, onSave }) {
  const appointmentLike = ["appointment", "resource"].includes(module.engine);
  return <div className="border border-border bg-card p-5 md:p-7 space-y-6 max-w-4xl">
    <div>
      <div className="font-display text-2xl">Guest presentation & rules</div>
      <p className="text-sm text-muted-foreground mt-1">Settings are stored even while the module is unlicensed.</p>
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      <Field label="Guest-facing title"><Input disabled={!canEdit} value={settings.guest_title || ""} onChange={(e) => setSettings((s) => ({...s, guest_title:e.target.value}))}/></Field>
      <Field label="Availability note"><Input disabled={!canEdit} value={settings.availability_note || ""} placeholder="Optional note shown to guests" onChange={(e) => setSettings((s) => ({...s, availability_note:e.target.value}))}/></Field>
    </div>
    <Field label="Guest introduction"><Textarea disabled={!canEdit} value={settings.guest_intro || ""} onChange={(e) => setSettings((s) => ({...s, guest_intro:e.target.value}))}/></Field>
    {appointmentLike && <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <Field label="Default duration"><Input disabled={!canEdit} type="number" min="0" value={settings.default_duration_min ?? 60} onChange={(e) => setSettings((s) => ({...s, default_duration_min:e.target.value}))}/></Field>
        <Field label="Slot interval"><Input disabled={!canEdit} type="number" min="0" value={settings.slot_interval_min ?? 15} onChange={(e) => setSettings((s) => ({...s, slot_interval_min:e.target.value}))}/></Field>
        <Field label="Preparation / reset (min)"><Input disabled={!canEdit} type="number" min="0" value={settings.prep_time_min ?? 15} onChange={(e) => setSettings((s) => ({...s, prep_time_min:e.target.value}))}/></Field>
        <Field label="Lead time (min)"><Input disabled={!canEdit} type="number" min="0" value={settings.booking_lead_min ?? 0} onChange={(e) => setSettings((s) => ({...s, booking_lead_min:e.target.value}))}/></Field>
        <Field label="Book ahead (days)"><Input disabled={!canEdit} type="number" min="0" value={settings.max_advance_days ?? 30} onChange={(e) => setSettings((s) => ({...s, max_advance_days:e.target.value}))}/></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 max-w-md">
        <Field label="Booking opens"><Input disabled={!canEdit} type="time" value={settings.open_time || "08:00"} onChange={(e) => setSettings((s) => ({...s, open_time:e.target.value}))}/></Field>
        <Field label="Booking closes"><Input disabled={!canEdit} type="time" value={settings.close_time || "22:00"} onChange={(e) => setSettings((s) => ({...s, close_time:e.target.value}))}/></Field>
      </div>
    </>}
    <div className="flex flex-wrap gap-6">
      <label className="flex items-center gap-2 text-sm"><Switch disabled={!canEdit} checked={!!settings.requires_confirmation} onCheckedChange={(v) => setSettings((s) => ({...s, requires_confirmation:v}))}/>Crew confirmation required</label>
      <label className="flex items-center gap-2 text-sm"><Switch disabled={!canEdit} checked={!!settings.allow_guest_notes} onCheckedChange={(v) => setSettings((s) => ({...s, allow_guest_notes:v}))}/>Allow guest notes</label>
      {module.engine === "order" && <label className="flex items-center gap-2 text-sm"><Switch disabled={!canEdit} checked={!!settings.show_prices} onCheckedChange={(v) => setSettings((s) => ({...s, show_prices:v}))}/>Show prices</label>}
    </div>
    {module.engine === "order" && <Field label="Currency"><Input disabled={!canEdit} className="max-w-40" value={settings.currency || "EUR"} onChange={(e) => setSettings((s) => ({...s, currency:e.target.value.toUpperCase()}))}/></Field>}
    {canEdit && <Button disabled={busy} onClick={onSave}><Save className="h-4 w-4 mr-2"/>Save module settings</Button>}
    {!canEdit && <div className="text-sm text-muted-foreground">Your role can view module setup but cannot change yacht configuration.</div>}
  </div>;
}
